/**
 * Why "Imam darilni bon" on the delivery step could not redeem a code, in
 * words a customer can act on — the gift-card equivalent of
 * `credit-problem.ts`'s `creditProblemMessage`, but for
 * `GiftCardRedeemController`'s own refusals (`POST /credit/redeem-code`)
 * rather than the claim-link ones that file already covers.
 *
 * Two of the answers are deliberately NOT duplicated here:
 *  - `ORDER_EMAIL_MISSING` reuses `credit.errorEmailMissing` — same fact,
 *    same fix (fill in the order's email), whichever credit endpoint hit it.
 *  - anything unrecognised (a new backend code this build does not know, or
 *    no code at all) falls back to `credit.errorUnavailable`, for the same
 *    reason `creditProblemMessage`'s `unavailable` exists: it must still be a
 *    sentence, never a blank box.
 *
 * `RATE_LIMITED` is not an `ApiError.code` the backend ever sends — the
 * throttler on `redeem-code` refuses with a bare Nest `ThrottlerException`,
 * carrying no body worth reading — so `giftCardRedeemErrorCode` stands the
 * 429 status in for one before it ever looks at `code`.
 */
import { creditProblemMessage } from "./credit-problem";

type Message = { key: string; fallback: string };

const MESSAGES: Record<string, Message> = {
  GIFT_CARD_NOT_FOUND: {
    key: "giftCard.redeemErrorNotFound",
    fallback: "Te kode ne poznamo. Preverite jo in poskusite znova.",
  },
  GIFT_CARD_WRONG_DOMAIN: {
    key: "giftCard.redeemErrorWrongDomain",
    fallback: "Ta bon velja na drugi spletni strani ({{domain}} ga ne sprejme).",
  },
  GIFT_CARD_SPENT: {
    key: "giftCard.redeemErrorSpent",
    fallback: "Ta bon je že porabljen.",
  },
  GIFT_CARD_CLAIMED: {
    key: "giftCard.redeemErrorClaimed",
    fallback: "Ta bon je že povezan z drugim e-naslovom. Pišite nam in uredimo.",
  },
  // The order is a gift-card purchase itself, or no longer a cart.
  GIFT_CARD_ORDER_NOT_REDEEMABLE: {
    key: "giftCard.redeemErrorOrderNotRedeemable",
    fallback: "Bona pri tem naročilu ni več mogoče uporabiti.",
  },
  // Payment has started, so the price is pinned and cannot take more credit.
  ORDER_PRICE_PINNED: {
    key: "giftCard.redeemErrorPricePinned",
    fallback: "Plačilo za to naročilo je že v teku, zato bona ni več mogoče dodati.",
  },
  RATE_LIMITED: {
    key: "giftCard.redeemErrorRateLimited",
    fallback: "Preveč poskusov. Poskusite čez minuto.",
  },
};

/**
 * @param code the backend's `ApiError.code` from a refused redeem (or
 *        `"RATE_LIMITED"` for a bare 429 — see `giftCardRedeemErrorCode`).
 *        Undefined or unrecognised falls back to `credit.errorUnavailable`.
 */
export function giftCardRedeemMessage(code?: string): Message {
  if (code === "ORDER_EMAIL_MISSING") return creditProblemMessage("email-missing");
  // Signed in to an unconfirmed account: the same sentence the credit box uses.
  if (code === "CREDIT_ACCOUNT_UNVERIFIED") return creditProblemMessage("account-unverified");
  if (code && MESSAGES[code]) return MESSAGES[code];
  return creditProblemMessage("unavailable");
}

/**
 * The code `giftCardRedeemMessage` wants, read off a failed
 * `POST /credit/redeem-code`.
 *
 * A throttled response carries no `code` field at all — Nest's
 * `ThrottlerException` is a bare 429 — so the status itself stands in for
 * one. Undefined (never null) when there was no response to read at all, as
 * with a network error, matching `creditProblemFromApiError`'s own shape for
 * "nothing to go on".
 */
export function giftCardRedeemErrorCode(error: unknown): string | undefined {
  const response = (
    error as { response?: { status?: number; data?: { code?: string } } } | null
  )?.response;
  if (!response) return undefined;
  if (response.status === 429) return "RATE_LIMITED";
  return response.data?.code;
}
